import {useApolloClient} from '@apollo/client';
import {StackScreenProps} from '@react-navigation/stack';
import {Auth} from 'aws-amplify';
import Constants from 'expo-constants';
import {Box, Button, Divider, HStack, Link, Switch, Text} from 'native-base';

import React, {useEffect, useState} from 'react';
import {ScrollView} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {Copiable} from '../components/Copiable';
import {Properties, PropertyList} from '../components/PropertyList';
import {useLockscreen} from '../hooks/useLockcreen';
import {useSettings} from '../hooks/useSettings';
import {useAccountContext} from '../lib/accountContext';
import {useUserContext} from '../lib/authContext';
import {
  BiometryType,
  getBiometryType,
  getBiometryTypeString,
  hasEnrolledBiometry,
  promptBiometry
} from '../lib/biometryUtils';
import {formatName, formatPhone} from '../lib/formatters';
import i18n from '../lib/i18n';
import {truncateHash} from '../lib/utils';
import {Nav, NavParams} from './navigation';
import {AccountStatus} from '../api/generated';

export const SettingsScreen: React.FC<StackScreenProps<NavParams, Nav.SettingsScreen>> = ({
  navigation
}) => {
  const {phoneNumber, ethAddress, signOut} = useUserContext();
  const account = useAccountContext();
  const client = useApolloClient();
  const [settings, setSettings] = useSettings();
  const [biometryEntryVisible, setBiometryEntryVisible] = useState(false);
  const [biometryEnabled, setBiometryEnabled] = useState(false);
  const {removePinFromStorage} = useLockscreen();
  const [biometryType, setBiometryType] = useState<BiometryType | undefined>();
  const isVerified = account?.status === AccountStatus.VERIFIED;

  useEffect(() => {
    (async () => {
      if (await hasEnrolledBiometry()) {
        const biometryMethod = await getBiometryType();
        setBiometryEntryVisible(true);
        setBiometryType(biometryMethod);
      }
    })();
  }, []);

  useEffect(() => {
    if (settings.useBiometricAuth && !biometryEnabled) {
      setBiometryEnabled(true);
    }
  }, [settings.useBiometricAuth, biometryEntryVisible]);

  const authenticateAsync = async () => {
    const authenticated = await promptBiometry(i18n.t('biometrySetPrompt'));
    if (authenticated) {
      await Auth.rememberDevice();
      setSettings({...settings, useBiometricAuth: true});
      return;
    } else {
      setBiometryEnabled(false);
    }
  };

  const toggleUseBiometricAuth = (newValue: boolean) => {
    setBiometryEnabled(newValue);
    if (newValue) {
      authenticateAsync();
    } else {
      setSettings({...settings, useBiometricAuth: false});
    }
  };

  const handleSignOut = async () => {
    signOut();
    removePinFromStorage();
    return client.clearStore();
  };

  const accountInfo = [
    {
      label: i18n.t('name'),
      value: formatName(account?.firstName, account?.lastName)
    },
    {
      label: i18n.t('phoneNumber'),
      value: formatPhone(phoneNumber)
    },
    {
      label: i18n.t('wallerAddress'),
      value: <Copiable value={ethAddress}>{truncateHash(ethAddress)}</Copiable>
    }
  ];

  const appSettings: Properties = [
    {
      label: i18n.t('useBiometricAuth', {biometryType: getBiometryTypeString(biometryType)}),
      value: biometryEntryVisible && biometryType && (
        <Switch
          isChecked={biometryEnabled}
          onValueChange={(value) => toggleUseBiometricAuth(value)}
        />
      )
    },
    {
      label: i18n.t('appVersion'),
      value: Constants.manifest?.version
    }
  ];

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <ScrollView>
        <HStack bg="gray.100" style={[t.pX5, t.pY3]} alignItems="center">
          <Text style={[t.flex1]}>{i18n.t('accountInfo')}</Text>
          <Link
            isUnderlined={false}
            _text={{color: 'primary.500'}}
            onPress={() => navigation.navigate(Nav.EditAccountScreen)}>
            {i18n.t('edit')}
          </Link>
        </HStack>
        <PropertyList properties={accountInfo} />
        <Divider style={t.mL5} />
        <Box bg="gray.100" style={[t.pY3, t.pX5]}>
          <Text>{i18n.t('appSettings')}</Text>
        </Box>
        <PropertyList properties={appSettings} />
      </ScrollView>
      <Button style={isVerified ? [t.m4] : [t.mX4, t.mT4]} variant="ghost" onPress={handleSignOut}>
        {i18n.t('signOut')}
      </Button>
      {!isVerified && (
        <Button style={[t.m4]} onPress={() => navigation.navigate(Nav.IdSessionScreen)}>
          {i18n.t('verifyIdentity')}
        </Button>
      )}
    </SafeAreaView>
  );
};
